/* eslint-disable jsx-a11y/role-supports-aria-props */
import React, { useEffect, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { Select } from 'antd';
import {
  AddressComponents,
  RequiredAddressPart,
  getFormattedAddressObject,
  getMissingAddressParts,
  isCompleteAddress,
  isFilledAddressPart,
} from '../../../utils/AddressUtils';
import InputWithPlacesAPI from './InputWithPlacesAPI';
import { US_STATES } from '../../../types/constants';

type AddressMode = 'autocomplete' | 'manual';

const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split('=')[1]) : null;
};

/**
 * The parts `isCompleteAddress` requires, named the way the customer would name them. Typed
 * against the list itself, so adding a required part is a compile error here until it has a name.
 */
const REQUIRED_PART_LABELS: Record<RequiredAddressPart, string> = {
  address_st_number: 'street number',
  address_st_name: 'street name',
  address_city: 'city',
  address_state: 'state',
  address_zip: 'ZIP code',
};

const joinWithAnd = (items: string[]): string =>
  items.length <= 2
    ? items.join(' and ')
    : `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;

interface FlexibleAddressInputProps {
  label?: string;
  /** Field that stores the full address string when using autocomplete (e.g. "businessAddress" or "address") */
  searchFieldName: string;

  /** Fields for normalized components */
  streetNumberFieldName: string; // e.g. "address_st_number"
  streetNameFieldName: string; // e.g. "address_st_name"
  unitFieldName?: string; // e.g. "address_unit"
  cityFieldName: string; // e.g. "address_city"
  stateFieldName: string; // e.g. "address_state"
  zipFieldName: string; // e.g. "address_zip"

  /** react-hook-form hooks */
  register: any;
  control: any;
  watch: any;
  setValue: any;
  errors: any;

  /** Optional: start in manual mode */
  initialMode?: AddressMode;

  /** placeholder for the autocomplete input */
  searchPlaceholder?: string;

  /** Requiredness for manual fields */
  required?: boolean;

  /**
   * Refuse a Google Places result that `isCompleteAddress` rejects (no street number, street name,
   * city, state or ZIP) instead of accepting it as-is. The customer is moved to the manual fields,
   * pre-filled with whatever Places did return, and told what is missing. The manual fields also
   * refuse a part that is only whitespace, on the field itself: `required` lets it through, and
   * the submit check that would catch it instead reports on the search bar, which is not
   * registered in manual mode. Off by default so the other callers of this input keep the
   * behaviour they have today.
   */
  requireCompleteAddress?: boolean;

  /** Notify parent whenever AddressComponents are updated */
  onAddressComponentsChange?: (components: AddressComponents) => void;
  addressSelectedFromDropdown: boolean;
  setAddressSelectedFromDropdown: (value: boolean) => void;
  cookieKey?: string;
}

/**
 * Reusable Address input that supports:
 * - Google Places autocomplete with dropdown + validation
 * - Manual entry with its own validation
 * - Toggle between them
 */
const FlexibleAddressInput: React.FC<FlexibleAddressInputProps> = ({
  label,
  searchFieldName,
  streetNumberFieldName,
  streetNameFieldName,
  unitFieldName,
  cityFieldName,
  stateFieldName,
  zipFieldName,
  register,
  control,
  watch,
  setValue,
  errors,
  initialMode = 'autocomplete',
  searchPlaceholder = 'Enter address',
  required = true,
  requireCompleteAddress = false,
  onAddressComponentsChange,
  addressSelectedFromDropdown,
  setAddressSelectedFromDropdown,
  cookieKey,
}) => {
  const cookieName = cookieKey || `address_mode_${String(searchFieldName)}`; // e.g. address_mode_businessAddress

  const [mode, setMode] = useState<AddressMode>(() => {
    const fromCookie = getCookie(cookieName);
    if (fromCookie === 'manual' || fromCookie === 'autocomplete') {
      return fromCookie;
    }
    return initialMode;
  });

  // Manual fields from RHF
  const wNum = watch(streetNumberFieldName);
  const wName = watch(streetNameFieldName);
  const wUnit = unitFieldName ? watch(unitFieldName) : undefined;
  const wCity = watch(cityFieldName);
  const wState = watch(stateFieldName);
  const wZip = watch(zipFieldName);

  // Set when a Places selection was refused, so the manual fields can say why they opened.
  const [incompleteNotice, setIncompleteNotice] = useState(false);

  const manualAddressComponents: AddressComponents = useMemo(
    () => ({
      address_st_number: wNum,
      address_st_name: wName,
      address_unit: wUnit,
      address_city: wCity,
      address_state: wState,
      address_zip: wZip,
    }),
    [wNum, wName, wUnit, wCity, wState, wZip]
  );

  // Whenever we’re in manual mode, push AddressComponents out to parent
  useEffect(() => {
    if (mode === 'manual' && onAddressComponentsChange) {
      onAddressComponentsChange(manualAddressComponents);
    }
  }, [mode, manualAddressComponents, onAddressComponentsChange]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const maxAge = 60 * 60 * 24 * 365; // 1 year
    document.cookie = `${cookieName}=${encodeURIComponent(
      mode
    )}; path=/; max-age=${maxAge}`;
  }, [mode, cookieName]);

  // A Places result the form cannot use: keep what it did return, clear the rest, and open the
  // manual fields so the customer can supply the missing parts.
  const rejectIncompleteSelection = (found: AddressComponents) => {
    // Every part is overwritten, not only the ones Places returned. A part left over from an
    // earlier, complete selection would otherwise ride along and make a mixed address look whole.
    setValue(streetNumberFieldName, found.address_st_number, {
      shouldDirty: true,
    });
    setValue(streetNameFieldName, found.address_st_name, {
      shouldDirty: true,
    });
    if (unitFieldName) {
      setValue(unitFieldName, found.address_unit, { shouldDirty: true });
    }
    setValue(cityFieldName, found.address_city, { shouldDirty: true });
    setValue(stateFieldName, found.address_state, { shouldDirty: true });
    setValue(zipFieldName, found.address_zip, { shouldDirty: true });
    // An empty bar that asks for a selection, not a filled one that looks answered.
    setValue(searchFieldName, '', { shouldDirty: true });

    setIncompleteNotice(true);
    setAddressSelectedFromDropdown(true);
    setMode('manual');
  };

  // Handle selection from Google Places
  const handleSelectAddress = (
    formattedAddress: string,
    addressComponents: any
  ) => {
    const formatted = addressComponents
      ? getFormattedAddressObject(addressComponents)
      : undefined;

    // Places omits the street number or route for rural roads, new construction and unlisted
    // addresses while still returning a city, so a result can look fine and still be unsendable.
    if (requireCompleteAddress && !isCompleteAddress(formatted)) {
      rejectIncompleteSelection(formatted ?? {});
      return;
    }

    setIncompleteNotice(false);

    // Set the top-level "full address" field
    setValue(searchFieldName, formattedAddress, {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (formatted) {
      // Hydrate manual fields so if user switches to manual it’s already filled
      if (formatted.address_st_number !== undefined) {
        setValue(streetNumberFieldName, formatted.address_st_number, {
          shouldDirty: true,
        });
      }
      if (formatted.address_st_name !== undefined) {
        setValue(streetNameFieldName, formatted.address_st_name, {
          shouldDirty: true,
        });
      }
      if (unitFieldName && formatted.address_unit !== undefined) {
        setValue(unitFieldName, formatted.address_unit, {
          shouldDirty: true,
        });
      }
      if (formatted.address_city !== undefined) {
        setValue(cityFieldName, formatted.address_city, {
          shouldDirty: true,
        });
      }
      if (formatted.address_state !== undefined) {
        setValue(stateFieldName, formatted.address_state, {
          shouldDirty: true,
        });
      }
      if (formatted.address_zip !== undefined) {
        setValue(zipFieldName, formatted.address_zip, {
          shouldDirty: true,
        });
      }

      if (onAddressComponentsChange) {
        onAddressComponentsChange(formatted);
      }
    }

    setAddressSelectedFromDropdown(true);
  };

  // Errors
  const fullFieldError =
    errors?.[searchFieldName]?.message &&
    String(errors?.[searchFieldName]?.message);
  const cityError =
    errors?.[cityFieldName]?.message &&
    String(errors?.[cityFieldName]?.message);
  const stateError =
    errors?.[stateFieldName]?.message &&
    String(errors?.[stateFieldName]?.message);
  const zipError =
    errors?.[zipFieldName]?.message && String(errors?.[zipFieldName]?.message);
  const streetNumError =
    errors?.[streetNumberFieldName]?.message &&
    String(errors?.[streetNumberFieldName]?.message);
  const streetNameError =
    errors?.[streetNameFieldName]?.message &&
    String(errors?.[streetNameFieldName]?.message);

  const isAutocompleteMode = mode === 'autocomplete';

  // Read live from the fields, so the message names only what is still missing and goes away once
  // the customer has filled it in.
  const missingPartLabels = getMissingAddressParts(manualAddressComponents).map(
    (part) => REQUIRED_PART_LABELS[part]
  );

  // Whitespace passes `required`, so with a complete address demanded each manual field also
  // checks the same thing `isCompleteAddress` does — and the form library owns (and clears) the
  // error, instead of the submit check leaving one behind on a field that is not on screen.
  const mustBeFilled = (message: string) =>
    requireCompleteAddress
      ? { validate: (value: unknown) => isFilledAddressPart(value) || message }
      : {};

  return (
    <div className="w-full">
      {/* LABEL */}
      {label?.length && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      {/* AUTOCOMPLETE MODE */}
      {isAutocompleteMode && (
        <>
          <InputWithPlacesAPI
            nameField={searchFieldName}
            placeholder={searchPlaceholder}
            register={register}
            addressSelectedFromDropdown={addressSelectedFromDropdown}
            setAddressSelectedFromDropdown={setAddressSelectedFromDropdown}
            watch={watch}
            setValue={setValue}
            defaultValue={watch(searchFieldName)}
            onSelectAddress={handleSelectAddress}
            error={fullFieldError}
          />

          <div className="mt-2 text-sm">
            <span className="mr-4 text-gray-500">
              Having trouble finding your address or have an address line 2?
            </span>
            <button
              type="button"
              onClick={() => {
                setAddressSelectedFromDropdown(true);
                setMode('manual');
              }}
              className="inline-flex items-center gap-1 text-sm font-medium text-infoPrimary hover:underline"
            >
              Enter address manually →
            </button>
          </div>
        </>
      )}

      {/* MANUAL MODE */}
      {!isAutocompleteMode && (
        <div className="mt-2 space-y-4">
          {incompleteNotice && missingPartLabels.length > 0 && (
            <p role="alert" className="text-sm text-red-500">
              {`We couldn't find a complete address for that result. Please add the ${joinWithAnd(missingPartLabels)} below.`}
            </p>
          )}

          <div className="mb-2 flex items-center">
            <span className="mr-4 text-sm text-gray-500">Want to go back?</span>
            <button
              type="button"
              onClick={() => {
                setIncompleteNotice(false);
                setMode('autocomplete');
              }}
              className="inline-flex items-center gap-1 text-sm font-medium text-infoPrimary hover:underline"
            >
              Back to search →
            </button>
          </div>

          {/* Street number + name */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Street Number
                {required && <span className="text-danger">*</span>}
              </label>
              <input
                {...register(streetNumberFieldName, {
                  required: required ? 'Street number is required' : false,
                  ...mustBeFilled('Street number is required'),
                })}
                placeholder="123"
                className={`mt-1 w-full rounded border px-3 py-2 ${
                  streetNumError ? 'border-red-500' : 'border-bordergray'
                }`}
              />
              {streetNumError && (
                <p className="mt-1 text-sm text-red-500">{streetNumError}</p>
              )}
            </div>

            <div className="md:col-span-4">
              <label className="block text-sm font-medium text-gray-700">
                Street Name
                {required && <span className="text-danger">*</span>}
              </label>
              <input
                {...register(streetNameFieldName, {
                  required: required ? 'Street name is required' : false,
                  ...mustBeFilled('Street name is required'),
                })}
                placeholder="Main Street"
                className={`mt-1 w-full rounded border px-3 py-2 ${
                  streetNameError ? 'border-red-500' : 'border-bordergray'
                }`}
              />
              {streetNameError && (
                <p className="mt-1 text-sm text-red-500">{streetNameError}</p>
              )}
            </div>
          </div>

          {/* Address line 2 (optional) */}
          {unitFieldName && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Address Line 2
              </label>
              <input
                {...register(unitFieldName)}
                placeholder="Apt 5B"
                className="mt-1 w-full rounded border border-bordergray px-3 py-2"
              />
            </div>
          )}

          {/* City */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              City
              {required && <span className="text-danger">*</span>}
            </label>
            <input
              {...register(cityFieldName, {
                required: required ? 'City is required' : false,
                ...mustBeFilled('City is required'),
              })}
              placeholder="Dallas"
              className={`mt-1 w-full rounded border px-3 py-2 ${
                cityError ? 'border-red-500' : 'border-bordergray'
              }`}
            />
            {cityError && (
              <p className="mt-1 text-sm text-red-500">{cityError}</p>
            )}
          </div>

          {/* ZIP */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              ZIP Code
              {required && <span className="text-danger">*</span>}
            </label>
            <input
              {...register(zipFieldName, {
                required: required ? 'ZIP code is required' : false,
                ...mustBeFilled('ZIP code is required'),
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  const onlyDigits = (e.target.value || '')
                    .replace(/\D/g, '')
                    .slice(0, 5);
                  e.target.value = onlyDigits;
                  setValue(zipFieldName, onlyDigits, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                },
              })}
              placeholder="75201"
              inputMode="numeric"
              autoComplete="postal-code"
              maxLength={5}
              className={`mt-1 w-full rounded border px-3 py-2 ${
                zipError ? 'border-red-500' : 'border-bordergray'
              }`}
              onKeyDown={(e) => {
                const allowed = [
                  'Backspace',
                  'Delete',
                  'Tab',
                  'ArrowLeft',
                  'ArrowRight',
                  'Home',
                  'End',
                ];
                if (allowed.includes(e.key)) return;
                if (!/^\d$/.test(e.key)) e.preventDefault();
              }}
            />
            {zipError && (
              <p className="mt-1 text-sm text-red-500">{zipError}</p>
            )}
          </div>

          {/* State */}
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700">
              State
              {required && <span className="text-danger">*</span>}
            </label>
            <Controller
              name={stateFieldName}
              control={control}
              rules={{
                required: required ? 'Please select a state' : false,
                ...mustBeFilled('Please select a state'),
              }}
              render={({ field }) => (
                <Select
                  className={`mt-1 block h-[38px] min-h-[38px] w-full ${
                    stateError ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Select a state"
                  options={US_STATES.map((state) => ({
                    value: state.abbreviation,
                    label: state.name,
                  }))}
                  {...field}
                />
              )}
            />
            {stateError && (
              <p className="mt-1 text-sm text-red-500">{stateError}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FlexibleAddressInput;
